import type { AssignableBrandConnection } from "./brandAssignableConnections";

export type BusinessGroupMemberAccessTarget = {
  connectionIdentities: string[];
  resourceType?: string;
  resourceKey?: string;
};

export type BusinessGroupMemberAccessActionAvailability = {
  enabled: boolean;
  disabledReason:
    | "permission-denied"
    | "loading"
    | "connection-unavailable"
    | "no-assignable-members"
    | null;
};

export type BusinessGroupApplicationAccessActionAvailability = {
  enabled: boolean;
  disabledReason:
    | "permission-denied"
    | "loading"
    | "no-applications"
    | "connection-unavailable"
    | null;
};

export const canOfferBusinessGroupWorkspaceInvite = ({
  canInviteWorkspaceMembers,
  assignableApplicationCount,
}: {
  canInviteWorkspaceMembers: boolean;
  assignableApplicationCount: number;
}) => canInviteWorkspaceMembers && assignableApplicationCount > 0;

type DatedApplication = {
  id: string;
  connectedAt: string;
};

const normalizeIdentity = (value?: string | number | null) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeResourceType = (value?: string | null) =>
  String(value || "")
    .trim()
    .toUpperCase();

const getConnectedTimestamp = (connectedAt: string) => {
  const normalized = connectedAt.trim();
  if (!normalized || normalized.toLowerCase() === "recent") {
    return null;
  }

  const timestamp = new Date(normalized).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

export const sortBusinessGroupApplicationsNewestFirst = <
  TApplication extends DatedApplication,
>(
  applications: TApplication[],
): TApplication[] =>
  applications
    .map((application, index) => ({
      application,
      index,
      timestamp: getConnectedTimestamp(application.connectedAt),
    }))
    .sort((left, right) => {
      if (left.timestamp !== null && right.timestamp !== null) {
        return right.timestamp - left.timestamp || left.index - right.index;
      }
      if (left.timestamp !== null) return -1;
      if (right.timestamp !== null) return 1;
      return left.index - right.index;
    })
    .map(({ application }) => application);

export const resolveBusinessGroupMemberAccessActionAvailability = ({
  canManage,
  isReady,
  hasConnection,
  assignableMemberCount,
}: {
  canManage: boolean;
  isReady: boolean;
  hasConnection: boolean;
  assignableMemberCount: number;
}): BusinessGroupMemberAccessActionAvailability => {
  if (!canManage) {
    return { enabled: false, disabledReason: "permission-denied" };
  }
  if (!isReady) {
    return { enabled: false, disabledReason: "loading" };
  }
  if (!hasConnection) {
    return { enabled: false, disabledReason: "connection-unavailable" };
  }
  if (assignableMemberCount === 0) {
    return { enabled: false, disabledReason: "no-assignable-members" };
  }

  return { enabled: true, disabledReason: null };
};

export const resolveBusinessGroupApplicationAccessActionAvailability = ({
  canManage,
  isReady,
  applicationCount,
  manageableApplicationCount,
}: {
  canManage: boolean;
  isReady: boolean;
  applicationCount: number;
  manageableApplicationCount: number;
}): BusinessGroupApplicationAccessActionAvailability => {
  if (!canManage) {
    return { enabled: false, disabledReason: "permission-denied" };
  }
  if (!isReady) {
    return { enabled: false, disabledReason: "loading" };
  }
  if (applicationCount === 0) {
    return { enabled: false, disabledReason: "no-applications" };
  }
  if (manageableApplicationCount === 0) {
    return { enabled: false, disabledReason: "connection-unavailable" };
  }

  return { enabled: true, disabledReason: null };
};

export const resolveBusinessGroupMemberAccessConnection = (
  connections: AssignableBrandConnection[],
  target: BusinessGroupMemberAccessTarget,
): AssignableBrandConnection | null => {
  const targetConnectionIdentities = new Set(
    target.connectionIdentities.map(normalizeIdentity).filter(Boolean),
  );
  const targetResourceType = normalizeResourceType(target.resourceType);
  const targetResourceKey = normalizeIdentity(target.resourceKey);
  const targetsExactResource = Boolean(targetResourceType && targetResourceKey);

  return (
    connections.find((connection) => {
      const connectionIdentities = [
        connection.id,
        connection.integrationConnectionId,
      ]
        .map(normalizeIdentity)
        .filter(Boolean);
      const matchesConnection = connectionIdentities.some((identity) =>
        targetConnectionIdentities.has(identity),
      );
      if (!matchesConnection) {
        return false;
      }

      if (!targetsExactResource) {
        return connection.assignmentScope === "connection";
      }

      return (
        normalizeResourceType(
          connection.resourceType || connection.resource?.resourceType,
        ) === targetResourceType &&
        normalizeIdentity(
          connection.resourceKey || connection.resource?.resourceKey,
        ) === targetResourceKey
      );
    }) || null
  );
};
